import { useAudioPlayer } from "expo-audio";
import type * as ExpoHaptics from "expo-haptics";
import { useCallback, useEffect, useState } from "react";
import { Image, Platform, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import { courseTheme, courseShadow } from "@/lib/training/courseTheme";
import type { Screen } from "@/lib/training/types";

// Same guard the recorder FAB uses — expo-haptics has no web implementation.
// eslint-disable-next-line @typescript-eslint/no-require-imports
const Haptics = Platform.OS === "web" ? null : (require("expo-haptics") as typeof ExpoHaptics);

/* eslint-disable @typescript-eslint/no-require-imports */
const CORRECT_SOUND = require("../../../assets/training/scope-correct.mp3");
const WRONG_SOUND = require("../../../assets/training/scope-wrong.mp3");
/* eslint-enable @typescript-eslint/no-require-imports */

type ScopeCheck = Extract<Screen, { kind: "scopeCheck" }>;
type Pick = "yes" | "no";

/**
 * The scope refresher — one long scroll rather than the paged flow the other
 * courses use.
 *
 * Every item is a photograph of an aide part-way through a task, or a named
 * certification against a duty. The reader decides allowed / not allowed, and
 * whichever way they answer the card ends with the same boundary statement:
 * what IS in scope and what is NOT. That is the teaching point, so it shows on
 * a right answer too.
 *
 * Answers are held here, not in the player, because the whole course is a
 * single screen — the footer turns into Submit once every item is answered,
 * and that submit is what completes the course and the bundle.
 */
export function ScopeCheckScreen({
    screen,
    onProgress
}: {
    screen: ScopeCheck;
    onProgress: (answered: number, total: number) => void;
}) {
    const [picks, setPicks] = useState<Record<number, Pick>>({});
    const answered = Object.keys(picks).length;
    const total = screen.items.length;

    // Feedback on answering. Both are deliberately quiet: the sounds are short
    // CC0 UI tones and playback follows the device's silent switch, because an
    // RN may well be standing in a client's living room.
    const correctSound = useAudioPlayer(CORRECT_SOUND);
    const wrongSound = useAudioPlayer(WRONG_SOUND);

    useEffect(() => {
        onProgress(answered, total);
    }, [answered, total, onProgress]);

    const choose = useCallback(
        (i: number, p: Pick) => {
            setPicks((prev) => {
                if (prev[i]) {
                    return prev;
                }
                const right = p === (screen.items[i].ok ? "yes" : "no");
                const sound = right ? correctSound : wrongSound;
                try {
                    sound.seekTo(0);
                    sound.play();
                } catch {
                    // Audio is a nicety — never let it break answering.
                }
                Haptics?.notificationAsync(
                    right
                        ? Haptics.NotificationFeedbackType.Success
                        : Haptics.NotificationFeedbackType.Warning
                ).catch(() => {});
                return { ...prev, [i]: p };
            });
        },
        [screen.items, correctSound, wrongSound]
    );

    return (
        <View>
            {screen.intro ? <Text style={styles.intro}>{screen.intro}</Text> : null}

            <View style={styles.counterRow}>
                <Text style={styles.counter}>{`${answered} of ${total} answered`}</Text>
                <View style={styles.track}>
                    <View
                        style={[
                            styles.trackFill,
                            { width: `${total > 0 ? (answered / total) * 100 : 0}%` }
                        ]}
                    />
                </View>
            </View>

            {screen.items.map((item, i) => {
                const pick = picks[i];
                const key: Pick = item.ok ? "yes" : "no";
                const right = pick === key;

                return (
                    <View key={i} style={styles.card}>
                        <Image
                            source={{ uri: item.image }}
                            style={styles.shot}
                            resizeMode="cover"
                        />

                        <View style={styles.body}>
                            <Text style={styles.duty}>{item.duty.toUpperCase()}</Text>
                            <Text style={styles.act}>{item.act}</Text>

                            <View style={styles.opts}>
                                {(["yes", "no"] as Pick[]).map((k) => {
                                    const chosen = pick === k;
                                    const isKey = k === key;
                                    return (
                                        <TouchableOpacity
                                            key={k}
                                            activeOpacity={0.85}
                                            disabled={pick != null}
                                            onPress={() => choose(i, k)}
                                            style={[
                                                styles.opt,
                                                chosen &&
                                                    (right ? styles.optRight : styles.optWrong),
                                                pick != null &&
                                                    !chosen &&
                                                    isKey &&
                                                    styles.optReveal,
                                                pick != null && !chosen && !isKey && styles.optDim
                                            ]}
                                        >
                                            <Text
                                                style={[
                                                    styles.optText,
                                                    chosen &&
                                                        (right
                                                            ? styles.optTextRight
                                                            : styles.optTextWrong),
                                                    pick != null &&
                                                        !chosen &&
                                                        isKey &&
                                                        styles.optTextRight
                                                ]}
                                            >
                                                {k === "yes" ? "Allowed" : "Not allowed"}
                                            </Text>
                                            <Text style={styles.optSub}>
                                                {k === "yes" ? "within scope" : "outside scope"}
                                            </Text>
                                        </TouchableOpacity>
                                    );
                                })}
                            </View>

                            {pick != null ? (
                                <View style={styles.answer}>
                                    <Text
                                        style={[
                                            styles.verdict,
                                            right ? styles.verdictOk : styles.verdictNo
                                        ]}
                                    >
                                        {right ? "✓ That's right" : "✗ Not this time"}
                                        {" · "}
                                        {item.ok
                                            ? "this one is fine"
                                            : "this is outside their scope"}
                                    </Text>
                                    <Text style={styles.rule}>{item.rule}</Text>

                                    <View style={[styles.scope, styles.scopeIn]}>
                                        <Text style={[styles.scopeLbl, styles.scopeLblIn]}>
                                            ✓ IN SCOPE
                                        </Text>
                                        <Text style={styles.scopeText}>{item.inScope}</Text>
                                    </View>
                                    <View style={[styles.scope, styles.scopeOut]}>
                                        <Text style={[styles.scopeLbl, styles.scopeLblOut]}>
                                            ✗ NOT IN SCOPE
                                        </Text>
                                        <Text style={styles.scopeText}>{item.outScope}</Text>
                                    </View>

                                    {item.watch ? (
                                        <Text style={styles.watch}>{item.watch}</Text>
                                    ) : null}
                                </View>
                            ) : null}
                        </View>
                    </View>
                );
            })}
        </View>
    );
}

const styles = StyleSheet.create({
    intro: {
        fontWeight: "500",
        fontSize: 15,
        lineHeight: 23,
        color: courseTheme.slate,
        marginBottom: 18
    },
    counterRow: { gap: 8, marginBottom: 20 },
    counter: {
        fontWeight: "700",
        fontSize: 13,
        color: courseTheme.slate
    },
    track: {
        height: 6,
        borderRadius: 3,
        backgroundColor: courseTheme.line,
        overflow: "hidden"
    },
    trackFill: { height: 6, borderRadius: 3, backgroundColor: courseTheme.teal },

    card: {
        backgroundColor: courseTheme.white,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: courseTheme.line,
        overflow: "hidden",
        marginBottom: 18,
        ...courseShadow.card
    },
    shot: { width: "100%", aspectRatio: 4 / 3, backgroundColor: courseTheme.paper },
    body: { padding: 16 },
    duty: {
        fontWeight: "800",
        fontSize: 11,
        letterSpacing: 1,
        color: courseTheme.tealD,
        marginBottom: 4
    },
    act: {
        fontWeight: "800",
        fontSize: 18,
        lineHeight: 24,
        color: courseTheme.navy,
        marginBottom: 14
    },

    opts: { flexDirection: "row", gap: 10 },
    opt: {
        flex: 1,
        alignItems: "center",
        paddingVertical: 14,
        borderRadius: 14,
        borderWidth: 2,
        borderColor: courseTheme.line,
        backgroundColor: courseTheme.white
    },
    optRight: { borderColor: courseTheme.green, backgroundColor: courseTheme.greenTint },
    optWrong: { borderColor: courseTheme.red, backgroundColor: courseTheme.redTint },
    optReveal: { borderColor: courseTheme.green, backgroundColor: courseTheme.greenTint },
    optDim: { opacity: 0.45 },
    optText: {
        fontWeight: "700",
        fontSize: 15,
        color: courseTheme.navy
    },
    optTextRight: { color: courseTheme.green },
    optTextWrong: { color: courseTheme.red },
    optSub: {
        fontWeight: "500",
        fontSize: 11,
        color: courseTheme.slateL,
        marginTop: 2
    },

    answer: {
        marginTop: 16,
        paddingTop: 16,
        borderTopWidth: 1,
        borderTopColor: courseTheme.line,
        gap: 10
    },
    verdict: { fontWeight: "800", fontSize: 14 },
    verdictOk: { color: courseTheme.green },
    verdictNo: { color: courseTheme.red },
    rule: {
        fontWeight: "700",
        fontSize: 16,
        lineHeight: 23,
        color: courseTheme.navy
    },

    scope: { borderRadius: 12, padding: 14, borderLeftWidth: 4 },
    scopeIn: { backgroundColor: courseTheme.greenTint, borderLeftColor: courseTheme.green },
    scopeOut: { backgroundColor: courseTheme.redTint, borderLeftColor: courseTheme.red },
    scopeLbl: {
        fontWeight: "800",
        fontSize: 11,
        letterSpacing: 1,
        marginBottom: 5
    },
    scopeLblIn: { color: courseTheme.green },
    scopeLblOut: { color: courseTheme.red },
    scopeText: {
        fontWeight: "600",
        fontSize: 16,
        lineHeight: 23,
        color: courseTheme.navy
    },
    watch: {
        fontWeight: "500",
        fontSize: 14,
        lineHeight: 21,
        color: courseTheme.slate
    }
});
